/**
 * Mock temporal de la trazabilidad de atenciones de un estudiante.
 *
 * Las atenciones vienen de fuentes que todavia no estan integradas:
 *   ips   -> desarrollaran su modulo en EMMA
 *   salud -> desarrollaran su modulo en EMMA
 *   acom  -> acompanamiento_docente, modulo que YA existe en EMMA (lider de bienestar / docente)
 *   bien  -> este mismo modulo de bienestar estudiantil
 *
 * Cuando se defina la integracion se reemplaza fetchTrazabilidadEstudiante
 * conservando la forma del objeto que retorna.
 */

export const ORIGEN_IPS = 'ips';
export const ORIGEN_SALUD = 'salud';
export const ORIGEN_ACOMPANAMIENTO = 'acom';
export const ORIGEN_BIENESTAR = 'bien';

// `reservado`: el diagnostico clinico no se muestra en Bienestar. El motivo y el
// cierre si, porque son el resumen general que pidio el area.
export const ORIGENES_TRAZABILIDAD = [
    { codigo: ORIGEN_BIENESTAR, nombre: 'Bienestar estudiantil', integrado: true, reservado: false },
    { codigo: ORIGEN_ACOMPANAMIENTO, nombre: 'Coordinadores de bienestar', integrado: false, reservado: false },
    { codigo: ORIGEN_IPS, nombre: 'IPS', integrado: false, reservado: true },
    { codigo: ORIGEN_SALUD, nombre: 'Salud', integrado: false, reservado: true },
];

export const origenEsReservado = (codigo) => {
    const origen = ORIGENES_TRAZABILIDAD.find((o) => o.codigo === codigo);
    return Boolean(origen && origen.reservado);
};

export const origenEstaIntegrado = (codigo) => {
    const origen = ORIGENES_TRAZABILIDAD.find((o) => o.codigo === codigo);
    return Boolean(origen && origen.integrado);
};

export const nombreOrigen = (codigo) => {
    const origen = ORIGENES_TRAZABILIDAD.find((o) => o.codigo === codigo);
    return origen ? origen.nombre : '';
};

export const PERIODO_TODOS = 'todos';
export const PERIODOS_TRAZABILIDAD = ['2026-1', '2025-2', '2025-1'];

// Solo aplica desde que hay cita: antes de Agendado no hay a que asistir.
export const ASISTIO = 'asistio';
export const NO_ASISTIO = 'no_asistio';
export const ASISTENCIA_PENDIENTE = 'pendiente';

export const ETIQUETA_ASISTENCIA = {
    [ASISTIO]: 'Asistió',
    [NO_ASISTIO]: 'No asistió',
    [ASISTENCIA_PENDIENTE]: 'Pendiente',
};

const PLANTILLAS = [
    {
        origen: ORIGEN_BIENESTAR, opcion: 'Crisis', remite: 'Docente', estado: 'Cierre', dias: 2,
        profesional: 'Psicología · Bienestar', asistencia: ASISTIO,
        motivo: 'Episodio de llanto sostenido durante clase, con alteración del sueño en las últimas semanas.',
        cierre: 'Se realizó contención emocional y se construyó un plan de seguridad. Queda en seguimiento semanal.',
    },
    {
        origen: ORIGEN_BIENESTAR, opcion: 'Aprender a Aprender', remite: 'Autogestión', estado: 'Atendido', dias: 3,
        profesional: 'Psicología · Bienestar', asistencia: ASISTIO,
        motivo: 'Dificultad para sostener la atención en sesiones de estudio mayores a quince minutos.',
        cierre: 'Se inició trabajo con técnica pomodoro y revisión espaciada. Continúa en proceso.',
    },
    {
        origen: ORIGEN_BIENESTAR, opcion: 'Comunícate', remite: 'Líder de bienestar', estado: 'Agendado', dias: null,
        profesional: 'Fonoaudiología · Bienestar', asistencia: ASISTENCIA_PENDIENTE,
        motivo: 'Bloqueos al exponer en público y pérdida de volumen de la voz.',
        cierre: '',
    },
    {
        origen: ORIGEN_BIENESTAR, opcion: 'PASPE socioemocional', remite: 'Autogestión', estado: 'Rechazado', dias: null,
        profesional: '', asistencia: null,
        motivo: 'Solicitud rechazada: el estudiante no completó la documentación requerida.',
        cierre: '',
    },
    {
        origen: ORIGEN_ACOMPANAMIENTO, opcion: 'Acompañamiento académico', remite: 'Docente', estado: 'Cierre', dias: 5,
        profesional: 'Docente · Ciencias Sociales y Humanas', asistencia: ASISTIO,
        motivo: 'Bajo rendimiento en el primer corte de dos asignaturas.',
        cierre: 'Se acordó plan de nivelación con la docente. El estudiante recuperó ambas asignaturas.',
    },
    {
        origen: ORIGEN_ACOMPANAMIENTO, opcion: 'Seguimiento a caso', remite: 'Líder de bienestar', estado: 'Atendido', dias: 1,
        profesional: 'Líder de bienestar · Facultad', asistencia: ASISTIO,
        motivo: 'Seguimiento al caso remitido por Bienestar el semestre anterior.',
        cierre: 'El estudiante reporta mejoría. Se mantiene contacto mensual.',
    },
    {
        origen: ORIGEN_IPS, opcion: 'Consulta psicológica', remite: 'Bienestar estudiantil', estado: 'Cierre', dias: 7,
        profesional: 'Psicología clínica · IPS', asistencia: ASISTIO,
        motivo: 'Remisión desde Bienestar por sintomatología ansiosa persistente.',
        cierre: 'Se indicó psicoterapia quincenal. El estudiante continúa en tratamiento.',
    },
    {
        origen: ORIGEN_IPS, opcion: 'Valoración inicial', remite: 'Líder de bienestar', estado: 'Agendado', dias: null,
        profesional: 'Medicina general · IPS', asistencia: ASISTENCIA_PENDIENTE,
        motivo: 'Valoración de ingreso solicitada por la líder de bienestar.',
        cierre: '',
    },
    {
        origen: ORIGEN_SALUD, opcion: 'Historia médica general', remite: 'Líder de bienestar', estado: 'Agendado', dias: null,
        profesional: 'Enfermería · Coordinación de Salud', asistencia: NO_ASISTIO,
        motivo: 'Cefaleas recurrentes y alteración del sueño reportadas en el primer corte.',
        cierre: 'No se presentó a la cita de control. Se reprogramará.',
    },
    {
        origen: ORIGEN_SALUD, opcion: 'Consulta de enfermería', remite: 'Autogestión', estado: 'Cierre', dias: 0,
        profesional: 'Enfermería · Coordinación de Salud', asistencia: ASISTIO,
        motivo: 'Consulta por malestar general durante la jornada académica.',
        cierre: 'Se brindó atención y se remitió a medicina general. Sin novedades posteriores.',
    },
];

const PROGRAMAS = ['Ingeniería de Sistemas', 'Psicología', 'Derecho', 'Administración de Empresas'];
const COHORTES = ['2021-1', '2022-1', '2022-2', '2023-1'];

// Semilla estable por documento: el mismo estudiante devuelve siempre lo mismo.
const semilla = (texto) => {
    let n = 0;
    for (let i = 0; i < String(texto || '').length; i += 1) {
        n = (n * 31 + String(texto).charCodeAt(i)) % 100000;
    }
    return n;
};

const formatearFecha = (fecha) => {
    const dd = String(fecha.getDate()).padStart(2, '0');
    const mm = String(fecha.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${fecha.getFullYear()}`;
};

// Bienestar ya no se simula: sus solicitudes vienen del API.
const PLANTILLAS_SIMULADAS = PLANTILLAS.filter((p) => p.origen !== ORIGEN_BIENESTAR);

const construirAtenciones = (persona, periodo) => {
    // El periodo entra en la semilla: sin eso cada periodo repetiria las mismas atenciones.
    const base = semilla(`${persona?.identificacion}${periodo}`);
    const cantidad = 2 + (base % 3);
    const anio = Number(String(periodo).split('-')[0]) || new Date().getFullYear();
    const mesInicial = String(periodo).endsWith('-1') ? 1 : 7;

    return Array.from({ length: cantidad }, (_, i) => {
        const plantilla = PLANTILLAS_SIMULADAS[(base + i * 3) % PLANTILLAS_SIMULADAS.length];
        const solicitud = new Date(anio, mesInicial + (i % 4), 3 + ((base + i * 7) % 24));
        const atencion = plantilla.dias === null
            ? null
            : new Date(solicitud.getTime() + plantilla.dias * 86400000);
        const fila = {
            id: `${persona?.identificacion || 'x'}-${periodo}-${i}`,
            periodo,
            origen: plantilla.origen,
            origen_nombre: nombreOrigen(plantilla.origen),
            opcion_seleccionada: plantilla.opcion,
            quien_remite: plantilla.remite,
            profesional: plantilla.profesional,
            reservado: origenEsReservado(plantilla.origen),
            estado: plantilla.estado,
            asistencia: plantilla.asistencia,
            motivo: plantilla.motivo,
            cierre: plantilla.cierre,
            // El diagnostico clinico todavia no se consume: la columna queda, el dato no.
            diagnostico: '',
            fecha_solicitud: formatearFecha(solicitud),
            fecha_atencion: atencion ? formatearFecha(atencion) : 'Sin atender',
            // Clave de orden: fecha_solicitud es dd/mm/yyyy y compararla como texto
            // ordena por dia antes que por anio.
            orden: solicitud.getTime(),
        };
        return fila;
    }).sort((a, b) => b.orden - a.orden);
};

const nombreCompleto = (p) => [p?.primer_nombre, p?.segundo_nombre, p?.primer_apellido, p?.segundo_apellido]
    .filter(Boolean).join(' ').trim();

export const periodoLegible = (periodo) => {
    const texto = String(periodo || '');
    return /^\d{5}$/.test(texto) ? `${texto.slice(0, 4)}-${texto.slice(4)}` : texto;
};

const fechaLegible = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

/**
 * Traduce una solicitud del API a la forma que consume la consulta, y le cuelga
 * el objeto completo para que el detalle reuse DetalleSolicitud.
 */
export const solicitudAAtencion = (s) => {
    const fechaAtencion = fechaLegible(s.fecha_atencion);
    let asistencia = null;
    if (s.asistio === true) asistencia = ASISTIO;
    else if (s.asistio === false) asistencia = fechaAtencion ? NO_ASISTIO : ASISTENCIA_PENDIENTE;
    return {
        id: `bien-${s.id}`,
        periodo: periodoLegible(s.periodo),
        origen: ORIGEN_BIENESTAR,
        origen_nombre: nombreOrigen(ORIGEN_BIENESTAR),
        opcion_seleccionada: s.tipo_servicio?.nombre || 'Sin servicio',
        quien_remite: s.quien_remite || 'Sin registrar',
        profesional: s.profesional_asignado
            ? `${s.profesional_asignado.primer_nombre || ''} ${s.profesional_asignado.primer_apellido || ''}`.trim()
            : '',
        reservado: false,
        estado: s.estado_actual?.nombre || '',
        asistencia,
        motivo: s.factor_riesgo?.nombre || s.motivo || '',
        cierre: s.texto_cierre || '',
        diagnostico: '',
        fecha_solicitud: fechaLegible(s.fecha_registro) || '',
        fecha_atencion: fechaAtencion || 'Sin atender',
        orden: s.fecha_registro ? new Date(s.fecha_registro).getTime() : 0,
        bienestar: s,
    };
};

export const fetchTrazabilidadEstudiante = ({ persona, periodo }) => new Promise((resolve) => {
    setTimeout(() => {
        if (!persona) {
            resolve(null);
            return;
        }
        const base = semilla(persona.identificacion);
        const periodos = periodo === PERIODO_TODOS ? PERIODOS_TRAZABILIDAD : [periodo];
        const atenciones = periodos
            .reduce((acum, p) => acum.concat(construirAtenciones(persona, p)), [])
            .sort((a, b) => b.orden - a.orden);
        resolve({
            estudiante: {
                identificacion: persona.identificacion || '',
                nombre: nombreCompleto(persona),
                correo: persona.correo || '',
                contacto: persona.celular || persona.telefono || `300 ${String(base).padStart(7, '0').slice(0, 7)}`,
                programa: persona.programa?.nombre || PROGRAMAS[base % PROGRAMAS.length],
                cohorte: COHORTES[base % COHORTES.length],
            },
            atenciones,
        });
    }, 400);
});
